
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedArea } from '../../../../../../redux/Slices/layoutSlice';

const AreaSelectorBar = ({ areas, toolsState, isDarkMode }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { selectedArea, layoutColor } = useSelector((state) => state.layout);

    if (!areas || areas.length === 0) return null;

    return (
        <div className={`w-full flex justify-center py-4 shrink-0 z-30 transition-all duration-300 animate-in slide-in-from-bottom-4 fade-in`}>
            <style>{`
                .thin-scrollbar::-webkit-scrollbar {
                    height: 4px;
                    width: 4px;
                }
                .thin-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .thin-scrollbar::-webkit-scrollbar-thumb {
                    background-color: rgba(156, 163, 175, 0.3);
                    border-radius: 20px;
                }
                .thin-scrollbar::-webkit-scrollbar-thumb:hover {
                    background-color: rgba(156, 163, 175, 0.6);
                }
            `}</style>
            <div className={`flex items-center gap-2 px-3 py-2 rounded-2xl shadow-lg backdrop-blur-md border ${
                isDarkMode 
                ? 'bg-slate-900/80 border-slate-700/50 shadow-slate-900/50' 
                : 'bg-white/80 border-gray-200/50 shadow-gray-200/50'
            }`}>
                
                <span className={`text-[10px] font-bold uppercase tracking-wider mr-2 ml-1 ${
                    isDarkMode ? 'text-slate-200' : 'text-slate-600'
                }`}>
                    {t('layouts.designer_select_area')}
                </span>

                <div className={`h-4 w-px bg-current mx-2 ${isDarkMode ? 'opacity-40' : 'opacity-30'}`}></div>

                <div className="flex items-center gap-4 overflow-x-auto max-w-[60vw] thin-scrollbar py-2 px-2">
                    {areas.map((area, index) => {
                        const isSelected = selectedArea?.id === area.id;
                        const areaColor = toolsState?.areaColors?.[area.id] || 
                                         ((layoutColor && layoutColor.length > 0) ? layoutColor[index % layoutColor.length] : '#64748b');
                        
                        // Parse opacity if needed, but for this mini-bar, fully opaque colors look better
                        // We will use the color directly.

                        return (
                            <button
                                key={area.id}
                                onClick={() => dispatch(setSelectedArea(area))}
                                className={`
                                    relative group flex flex-col items-center justify-center
                                    w-10 h-10 rounded-xl transition-all duration-300
                                    ${isSelected 
                                        ? 'scale-110 shadow-md ring-2 ring-offset-2 ring-offset-transparent' 
                                        : 'hover:scale-105 hover:bg-black/5 opacity-80 hover:opacity-100'
                                    }
                                `}
                                style={{
                                    backgroundColor: isSelected ? areaColor : (isDarkMode ? 'rgba(30, 41, 59, 0.7)' : 'rgba(241, 245, 249, 1)'),
                                    borderColor: isSelected ? areaColor : (isDarkMode ? 'rgba(71, 85, 105, 0.5)' : 'rgba(203, 213, 225, 1)'),
                                    borderWidth: '2px',
                                    color: isSelected ? '#ffffff' : (isDarkMode ? '#e2e8f0' : '#475569'),
                                    '--tw-ring-color': areaColor
                                }}
                                title={`Select Zone ${area.order ?? index + 1}`}
                            >
                                {/* Indicator Dot for active state - Professional Design */}
                                {isSelected && (
                                    <span className="absolute -top-2 -right-2 w-5 h-5 bg-white rounded-full flex items-center justify-center shadow-lg transform scale-100 transition-transform duration-300 z-20 ring-2 ring-white"
                                          style={{ color: areaColor }}
                                    >
                                        <i className="bx bxs-check-circle text-base"></i>
                                    </span>
                                )}

                                <span className={`text-xs font-bold ${
                                    isSelected 
                                    ? 'drop-shadow-sm' 
                                    : ''
                                }`}>
                                    {area.order ?? index + 1}
                                </span>
                                
                                {/* Tooltip on hover */}
                                <div className={`absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 rounded-md text-[10px] whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-sm ${
                                    isDarkMode ? 'bg-slate-800 text-white' : 'bg-gray-800 text-white'
                                }`}>
                                    {area.name || `Zone ${area.order ?? index + 1}`}
                                    <div className={`absolute bottom-[-4px] left-1/2 -translate-x-1/2 border-4 border-transparent ${
                                        isDarkMode ? 'border-t-slate-800' : 'border-t-gray-800'
                                    }`}></div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default AreaSelectorBar;
